/**
 * 학생 상세 탭 본문(설계 §4-1) — 개요 · 반 편성 · 출결 · 학교·부서 · 메모 · 이력.
 * 상세 묶음(S2)은 패널이 한 번 받아 넘긴다. 메모(N1) · 이력(N4) · 출결(AT5)은 그 탭을 열 때 부른다.
 * 쓰기 — 매니저는 개요 카드를 누르거나 줄의 [⋯]·단추로 창(modals/StudentActionModal)을 연다. onAction 이 없으면(강사) 읽기만.
 * 서버에 아직 없는 작업(stageGate)은 카드를 누를 수 없게 · 단추 대신 「n단계에 열림」으로 둔다. 출결 탭 잠금은 패널이 한다.
 */
import React, { useMemo, useState } from 'react';
import { AcademyButton, ActionButton } from '../controls';
import { Pencil } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getStudentAttendance, getStudentHistory, listNotes } from '../academyApi';
import { buildLanes, enrollmentRows, historyGroupOf, historyTone, HISTORY_GROUPS, stateBadge } from '../academyModel';
import {
  addDays, elapsedDays, formatMonthDay, isoWeekday, todayKST, weekdayLabel,
} from '../periodDates';
import { classActions, stateActions } from '../modals/StudentActionModal';
import { Box, ErrorNotice, MenuButton, SegTabs, Spinner, ToneBadge, useAcademyVersion, useAsync } from '../ui';
import NoteList from './NoteList';
import NotesPanel from './NotesPanel';
import PeriodLanes from './PeriodLanes';

const span = (p) => `${p.start_on || '?'} ~ ${p.end_on || ''}`;

// ── 개요 ──

/** 개요 카드 — 매니저는 눌러서 그 값을 바꾼다(오너 2026-10-10 「각 카드는 각각 선택했을 때 수정할 수 있도록」) */
function InfoCard({ label, value, sub, onEdit }) {
  const body = (
    <>
      <p className="flex items-center gap-1 text-xs text-gray-500">
        {label}
        {onEdit && <Pencil aria-hidden="true" className="ml-auto size-3 text-gray-300 group-hover:text-primary" />}
      </p>
      <p className="truncate text-sm font-semibold text-gray-900" title={value}>{value}</p>
      <p className="truncate text-xs text-gray-500" title={sub}>{sub || ' '}</p>
    </>
  );
  if (!onEdit) return <div className="min-w-0 rounded-2xl border border-gray-200 px-2.5 py-2">{body}</div>;
  return (
    <button type="button" onClick={onEdit} aria-label={`${label} 바꾸기`} className="group min-w-0 rounded-2xl border border-gray-200 px-2.5 py-2 text-left transition-colors hover:border-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      {body}
    </button>
  );
}

/**
 * 학생 정보 — 출결번호 · 로그인 이메일 · 연락처 · 로그인 잠김(백엔드 2단계 전달 §2 profile).
 * 매니저는 [정보 수정]로 이름 · 이메일 · 비밀번호 · 연락처 · 출결번호를 바꾼다(§4-B).
 */
function ProfileBox({ profile, onEdit }) {
  const rows = [
    ['출결번호', profile.student_no || '—'],
    ['로그인 이메일', profile.student_email || '—'],
    ['학생 연락처', profile.mobile_number || '—'],
    ['학부모 연락처', profile.parent_mobile_number || '—'],
  ];
  return (
    <Box
      title="학생 정보"
      sub={profile.is_deleted ? '삭제된 계정' : ''}
      right={onEdit && !profile.is_deleted && <AcademyButton type="button" onClick={onEdit}>정보 수정</AcademyButton>}
    >
      {profile.is_blocked && (
        <p role="status" className="mb-2 rounded-xl bg-red-50 px-3 py-1.5 text-xs text-danger">로그인 잠김 — 비밀번호를 새로 정하면 풀립니다.</p>
      )}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs md:grid-cols-4">
        {rows.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-gray-500">{k}</dt>
            <dd className="truncate font-medium text-gray-900" title={v}>{v}</dd>
          </div>
        ))}
      </dl>
    </Box>
  );
}

export function OverviewTab({ detail, asOf, onAction }) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const lanes = useMemo(() => buildLanes(detail, asOf), [detail, asOf]);
  const notes = useAsync((signal) => listNotes({ studentId: detail.student_id, signal }, getAccessToken), `notes-${detail.student_id}-${version}`);
  const c = detail.current;
  const b = stateBadge(c);
  const tenure = detail.tenures.find((t) => t.id === c.tenure?.id) || detail.tenures[detail.tenures.length - 1];
  // 휴원은 상태의 일부라 상태 카드에 합친다(오너 2026-10-10)
  const openPause = [...detail.pauses].reverse().find((p) => p.start_on <= asOf && (!p.end_on || p.end_on > asOf));
  const lastPause = detail.pauses[detail.pauses.length - 1];
  const pauseDays = detail.pauses.reduce((sum, p) => sum + elapsedDays(p.start_on, p.end_on, asOf), 0);
  let stateSub = tenure ? `${tenure.seq && detail.tenures.length > 1 ? `${tenure.seq}차 ` : ''}재원 ${formatMonthDay(tenure.start_on)}~` : '';
  // 복귀 예정일은 없다 — 끝이 있는 열린 휴원은 퇴원 예정일에 함께 닫힌 것(auto_closed)뿐이다(백엔드 5단계 전달 §1 · §4-D)
  if (openPause) stateSub = `휴원 ${formatMonthDay(openPause.start_on)}~${openPause.auto_closed ? ' · 퇴원 예정일까지' : ''}`;
  else if (lastPause) stateSub += ` · 휴원 ${pauseDays}일(${formatMonthDay(lastPause.start_on)}~${lastPause.end_on ? formatMonthDay(lastPause.end_on) : ''})`;
  const edit = (key) => (onAction ? () => onAction(key) : undefined);
  // 카드는 할 수 있는 작업이 하나라도 있을 때만 누를 수 있다 — 없으면 글만 보인다
  const canOpen = (actions) => actions.length > 0;
  const classSub = c.classes.map((x) => x.teacher?.name || '담임 없음').join(' · ');
  return (
    <div className="space-y-3">
      <Box>
        <p className="mb-3 text-xs text-gray-500">{lanes.rangeLabel}</p>
        <PeriodLanes key={detail.student_id} model={lanes} />
      </Box>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <InfoCard label="상태" value={b.date ? `${b.label} ${b.date}` : b.label} sub={stateSub} onEdit={canOpen(stateActions(detail)) ? edit('state') : undefined} />
        <InfoCard label="수강반" value={c.classes.map((x) => x.class_name).join(', ') || '반 없음'} sub={classSub} onEdit={canOpen(classActions(detail)) ? edit('class') : undefined} />
        <InfoCard label="학교·학년" value={`${c.school?.name || '학교 미지정'} ${c.grade_label}`} sub={c.needs_school_update ? '학교 갱신 필요' : ''} onEdit={edit('school')} />
        <InfoCard label="부서" value={c.department?.name || '부서 없음'} sub="" onEdit={edit('department')} />
      </div>
      <ProfileBox profile={detail.profile} onEdit={edit('profile')} />
      <Box title="최근 메모">
        {notes.loading && !notes.data ? <Spinner label="메모 불러오는 중" /> : notes.error
          ? <ErrorNotice error={notes.error} onRetry={notes.reload} compact />
          : <NoteList notes={notes.data.items} limit={3} />}
      </Box>
    </div>
  );
}

// ── 반 편성 ──

/**
 * 반 편성 탭 — 입반 · 반 이동 · 퇴반 · 반 편성 정정·삭제.
 * [반 이동] · [퇴반]은 진행 중이거나 퇴원 예정으로만 닫힌 줄(movable)에만 — 앞으로 퇴반 · 이동이 잡힌 줄은 서버가 409 로
 * 거절한다(날짜는 정정으로, 백엔드 4단계 전달 §4-C).
 */
export function EnrollmentsTab({ detail, asOf, onAction }) {
  const rows = enrollmentRows(detail.enrollments, asOf);
  const active = ['enrolled', 'paused', 'register_scheduled'].includes(detail.current.state);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {onAction && active && <AcademyButton type="button" onClick={() => onAction('enroll')}>입반</AcademyButton>}
      </div>
      <Box>
        {rows.length === 0 ? <p className="py-2 text-xs text-gray-500">반 편성 기록이 없습니다.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 font-medium">반</th>
                  <th className="py-1 font-medium">담임</th>
                  <th className="py-1 font-medium">입반일</th>
                  <th className="py-1 font-medium">퇴반일</th>
                  <th className="py-1 text-right font-medium">일수</th>
                  <th className="py-1 pl-3 font-medium">퇴반 이유</th>
                  {onAction && <th className="w-8 py-1" aria-label="작업" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const items = onAction ? [
                    ...(r.movable ? [
                      { key: 'transfer', label: '반 이동', onSelect: () => onAction('transfer', { preset: { enrollment_id: r.id } }) },
                      { key: 'end', label: '퇴반', onSelect: () => onAction('endEnrollment', { preset: { ...r, enrollment_id: r.id } }) },
                      { key: 'd1', divider: true },
                    ] : []),
                    { key: 'correct', label: '정정', onSelect: () => onAction('correct', { kind: 'enrollment', preset: r }) },
                    { key: 'delete', label: '삭제', danger: true, onSelect: () => onAction('delete', { kind: 'enrollment', preset: r }) },
                  ] : [];
                  return (
                    <tr key={r.id ?? `${r.class_id}-${r.start_on}`} className="border-t border-gray-100">
                      <td className={`py-1.5 ${r.current ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{r.class_name}</td>
                      <td className="py-1.5 text-gray-700">{r.teachersText}</td>
                      <td className="py-1.5 font-mono text-gray-700">{r.start_on}</td>
                      <td className="py-1.5 font-mono text-gray-700">{r.last_day || '—'}</td>
                      <td className="py-1.5 text-right text-gray-900">{r.days}</td>
                      <td className="py-1.5 pl-3"><ToneBadge tone={r.endLabel.tone}>{r.endLabel.text}</ToneBadge></td>
                      {onAction && (
                        <td className="py-1.5 text-right">
                          <MenuButton label="⋯" items={items} align="right" />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Box>
    </div>
  );
}

// ── 출결(AT5) ──

const STATUS_BADGE = { PRESENT: ['출석', 'ok'], LATE: ['지각', 'warn'], EARLY_LEAVE: ['조퇴', 'warn'], ABSENT: ['결석', 'danger'] };

export function AttendanceTab({ detail, canInput, onInput }) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const today = todayKST();
  const from = addDays(today, -55);
  const r = useAsync((signal) => getStudentAttendance(detail.student_id, { from, to: today, signal }, getAccessToken), `att-${detail.student_id}-${version}`);
  const d = r.data;
  return (
    <div className="space-y-3">
      {r.loading && !d && <Spinner label="출결 불러오는 중" />}
      {r.error && <ErrorNotice error={r.error} onRetry={r.reload} />}
      {d && (
        <>
          <Box title="반별 집계" sub={`${formatMonthDay(from)} ~ 오늘`}>
            {d.classes.length === 0 ? <p className="py-2 text-xs text-gray-500">이 기간에 수업이 없습니다.</p> : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="py-1 text-left font-medium">반</th>
                    {['출석', '지각', '조퇴', '결석', '미입력', '인정 시간'].map((h) => <th key={h} className="py-1 text-right font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {d.classes.map((c) => (
                    <tr key={c.class_id} className="border-t border-gray-100">
                      <td className="py-1.5 text-gray-900">{c.class_name}</td>
                      <td className="py-1.5 text-right">{c.counts.present}</td>
                      <td className="py-1.5 text-right">{c.counts.late}</td>
                      <td className="py-1.5 text-right">{c.counts.early_leave}</td>
                      <td className="py-1.5 text-right">{c.counts.absent}</td>
                      <td className={`py-1.5 text-right ${c.counts.unrecorded ? 'text-yellow-800' : ''}`}>{c.counts.unrecorded}</td>
                      <td className="py-1.5 text-right font-semibold">{Math.round(c.recognized_hours * 10) / 10}시간</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Box>
          <Box title="최근 출결">
            <ul className="divide-y divide-gray-100">
              {d.items.length === 0 && <li className="py-2 text-xs text-gray-500">기록이 없습니다.</li>}
              {d.items.map((it) => {
                const badge = it.paused ? ['휴원 — 대상 아님', 'muted'] : it.status === 'canceled' ? ['휴강', 'muted'] : it.status_code ? STATUS_BADGE[it.status_code] : ['미입력', 'warn'];
                const missing = !it.paused && it.status !== 'canceled' && !it.status_code;
                return (
                  <li key={`${it.class_id}-${it.date}-${it.start_time}`} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
                    <span className="w-20 shrink-0 font-mono text-gray-500">{formatMonthDay(it.date)}({weekdayLabel(isoWeekday(it.date))})</span>
                    <span className="text-gray-700">{it.class_name}{it.memo ? ` · ${it.memo}` : ''}</span>
                    <span className="ml-auto"><ToneBadge tone={badge[1]}>{badge[0]}</ToneBadge></span>
                    {missing && canInput(it.class_id) && <AcademyButton type="button" onClick={() => onInput(it)}>출결 입력</AcademyButton>}
                  </li>
                );
              })}
            </ul>
          </Box>
        </>
      )}
    </div>
  );
}

// ── 학교·부서 ──

/**
 * 학교·학년 — 학년은 **기간 행의 값 그대로** 보인다(grade_at_start). 자동 올림이 없어 「지금 학년 = 시작 학년 + 올림 n번」
 * 같은 계산도, 다음 올림일 미리보기도 없다(오너 결정 2026-10-12, 백엔드 0단계 전달 §3-1). 학년이 바뀌면 [학교·학년 변경]으로 새 기간.
 * 학년 이름은 서버의 grade_label 을 먼저 쓴다 — 14 = 「비재학」(백엔드 2단계 전달 §4-D).
 */
export function SchoolTab({ detail, onAction }) {
  const c = detail.current;
  const rows = [...detail.school_periods].sort((a, b) => String(b.start_on).localeCompare(String(a.start_on)));
  const depts = [...detail.department_periods].sort((a, b) => String(b.start_on).localeCompare(String(a.start_on)));
  const rowActions = (kind, p) => onAction && (
    <span className="flex justify-end gap-2">
      <ActionButton iconOnly type="button" onClick={() => onAction('correct', { kind, preset: p })}>정정</ActionButton>
      <ActionButton iconOnly type="button" onClick={() => onAction('delete', { kind, preset: p })} danger>삭제</ActionButton>
    </span>
  );
  return (
    <div className="space-y-3">
      {c.needs_school_update && (
        <p role="status" className="rounded-2xl bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
          학교 갱신 필요 · {c.grade_label}
          {onAction && <AcademyButton type="button" onClick={() => onAction('school')}>학교·학년 변경</AcademyButton>}
        </p>
      )}
      <Box
        title="학교·학년"
        right={onAction && <AcademyButton type="button" onClick={() => onAction('school')}>학교·학년 변경</AcademyButton>}
      >
        {rows.length === 0 ? <p className="py-2 text-xs text-gray-500">학교·학년 기록이 없습니다.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[440px] text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 font-medium">기간</th>
                  <th className="py-1 font-medium">학교</th>
                  <th className="py-1 font-medium">학년</th>
                  {onAction && <th className="py-1" aria-label="작업" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id ?? p.start_on} className="border-t border-gray-100">
                    <td className="py-1.5 font-mono text-gray-700">{span(p)}{p.estimated && <span className="ml-1 text-gray-400">추정</span>}</td>
                    <td className={`py-1.5 ${p.school ? 'text-gray-900' : 'text-gray-400'}`}>{p.school?.name || '학교 미지정'}</td>
                    <td className="py-1.5 text-gray-700">{p.grade_at_start != null ? `${p.grade_at_start} · ${p.grade_label}` : '—'}</td>
                    {onAction && <td className="py-1.5">{rowActions('school', p)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>
      <Box title="부서" right={onAction && <AcademyButton type="button" onClick={() => onAction('department')}>부서 변경</AcademyButton>}>
        {depts.length === 0 ? <p className="py-2 text-xs text-gray-500">부서 기록이 없습니다.</p> : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-1 font-medium">기간</th>
                <th className="py-1 font-medium">부서</th>
                <th className="py-1 font-medium">출처</th>
                {onAction && <th className="py-1" aria-label="작업" />}
              </tr>
            </thead>
            <tbody>
              {depts.map((p) => (
                <tr key={p.id ?? p.start_on} className="border-t border-gray-100">
                  <td className="py-1.5 font-mono text-gray-700">{span(p)}</td>
                  <td className="py-1.5 text-gray-900">{p.department?.name || '부서 없음'}</td>
                  <td className="py-1.5 text-gray-500">{p.source === 'v1_import' || p.estimated ? '이관(추정)' : '직접 입력'}</td>
                  {onAction && <td className="py-1.5">{rowActions('department', p)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>
    </div>
  );
}

// ── 메모 ──

export function NotesTab({ detail, isTeacher, myStaffId }) {
  // 강사는 범위 학생 메모를 쓰고 본인 메모만 고치고 지운다. 매니저는 모두(요청서 §10)
  return <NotesPanel target={{ studentId: detail.student_id }} canWrite={!detail.profile.is_deleted} canEdit={(n) => !detail.profile.is_deleted && (!isTeacher || (myStaffId != null && n.author_staff_id === myStaffId))} />;
}

// ── 이력 ──

const DOT = {
  ok: 'bg-primary',
  danger: 'bg-danger',
  info: 'bg-blue-500',
  warn: 'bg-yellow-400',
  muted: 'bg-gray-400',
  fix: 'border-2 border-yellow-500 bg-white',
};

export function HistoryTab({ detail, isTeacher = false }) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const [group, setGroup] = useState('all');
  // 강사에게는 서버가 정정 · 삭제 줄을 빼고 준다 — 늘 비는 「정정」 묶음은 감춘다(백엔드 8단계 전달 §2 · §4-I)
  const groups = HISTORY_GROUPS.filter((g) => !(isTeacher && g.key === 'correction'));
  const hist = useAsync((signal) => getStudentHistory(detail.student_id, { signal }, getAccessToken), `history-${detail.student_id}-${version}`);
  const items = (hist.data?.items || []).filter((it) => group === 'all' || historyGroupOf(it) === group);
  return (
    <div className="space-y-2">
      <div className="flex">
        <SegTabs tabs={groups.map((g) => [g.key, g.label])} value={group} onChange={setGroup} ariaLabel="이력 종류" />
      </div>
      <Box>
        {hist.loading && !hist.data ? <Spinner label="이력 불러오는 중" /> : hist.error
          ? <ErrorNotice error={hist.error} onRetry={hist.reload} compact />
          : items.length === 0 ? <p className="py-2 text-xs text-gray-500">이 종류의 기록이 없습니다.</p> : (
            <ol className="space-y-1">
              {items.map((it, i) => (
                // 같은 날 같은 종류가 둘 이상일 수 있어 순번을 열쇠에 섞는다
                <li key={`${it.date}-${it.type}-${i}`} className="grid grid-cols-[76px_14px_minmax(0,1fr)] gap-1.5 py-1 text-xs">
                  <span className="font-mono text-gray-500">{it.date}</span>
                  <span className={`mt-0.5 inline-block size-2.5 rounded-full ${DOT[historyTone(it)]}`} />
                  <span className={it.is_future ? 'text-gray-500' : 'text-gray-900'}>
                    {it.title}
                    {it.is_future && <span className="ml-1"><ToneBadge tone="info">예정</ToneBadge></span>}
                    {it.actor && <span className="text-gray-400"> · {it.actor}</span>}
                  </span>
                </li>
              ))}
            </ol>
          )}
      </Box>
    </div>
  );
}
